import { palettePrimaryMain, surfaceAuthCanvas, surfaceAuthCard } from '@/theme/designTokens'

/** Figma 5131-11088 — provider home */
export const homePageBg = surfaceAuthCanvas
export const homeHeroNavy = '#132640'
export const homeHeroGreeting = '#F8F9FF'
export const homeHeroWelcome = palettePrimaryMain
export const homeHeading = '#0F1729'
export const homeMuted = '#4A5565'
export const homeNavMuted = '#64748B'
export const homeCardBorder = '#E2E8F0'
export const homeStatLabel = '#4A5565'
export const homeStatValue = '#101828'
export const homeStatOrangeBg = '#FFF3E0'
export const homeStatOrangeIcon = '#F59E0B'
export const homeStatPurpleBg = '#F3E5F5'
export const homeStatPurpleIcon = '#BC23A2'
export const homeStatRedBg = '#FFEBEE'
export const homeStatRedIcon = '#FF5959'
export const homeTimePillBg = 'rgba(6, 182, 212, 0.14)'
export const homeTimePillBorder = '#06B6D4'
export const homeTimePillText = '#06B6D4'
export const homeEarnPillBg = 'rgba(204, 229, 255, 0.3)'
export const homeEarnPillBorder = '#CCE5FF'
export const homeEarnLabel = '#006192'
export const homeEarnAmount = '#04183F'
export const homeButtonShadow = '0px 4px 2px #A0AED3'
export const homePromoBadge = '#3B82F6'
export const homeFooterBg = '#F8FAFC'
export const homeFooterBorder = '#E2E8F0'
export const homeFooterText = '#64748B'

/** Figma 5131-11088 artboard width */
export const homeContentMaxWidth = 1280
/** Figma content column — stats, surveys, promo (~x:204, w:884) */
export const homeContentInnerMaxWidth = 884

export const homePageRootSx = {
  bgcolor: homePageBg,
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
} as const

/** Outer shell — aligns with nav (1280 + 40px side padding) */
export const homeContentShellSx = {
  width: '100%',
  maxWidth: homeContentMaxWidth,
  mx: 'auto',
  px: { xs: 2, sm: 3, md: '40px' },
  boxSizing: 'border-box',
} as const

/** Centered main column — hero copy, stats, survey grid, mobile promo */
export const homeContentInnerSx = {
  width: '100%',
  maxWidth: homeContentInnerMaxWidth,
  mx: 'auto',
  boxSizing: 'border-box',
} as const

/** @deprecated Use homeContentShellSx + homeContentInnerSx on home */
export const homeContentWrapSx = homeContentShellSx

/** Figma — navy greeting band height (Home, Rewards, Select Brand, etc.) */
export const providerShellHeroHeightPx = 235

/** Shared navy hero — fixed height, greeting vertically centered */
export const providerShellHeroSx = {
  bgcolor: homeHeroNavy,
  backgroundColor: homeHeroNavy,
  minHeight: { xs: 180, md: `${providerShellHeroHeightPx}px` },
  height: { md: `${providerShellHeroHeightPx}px` },
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
} as const

export const providerShellHeroShellSx = {
  ...homeContentShellSx,
  width: '100%',
} as const

/** @deprecated Use providerShellHeroSx */
export const homeHeroSx = providerShellHeroSx

export const homeHeroGreetingSx = {
  fontWeight: 700,
  fontSize: { xs: '28px', md: '40px' },
  lineHeight: { xs: '34px', md: '40px' },
  letterSpacing: '0.4063px',
  color: homeHeroGreeting,
  overflowWrap: 'anywhere',
} as const

export const homeHeroWelcomeSx = {
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '28px',
  color: homeHeroWelcome,
  mt: 1,
} as const

export const homeStatsRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
  gap: '25px',
  mt: { xs: -6, md: '-52px' },
  mb: 4,
  position: 'relative',
  zIndex: 1,
} as const

export const homeStatCardSx = {
  bgcolor: surfaceAuthCard,
  borderRadius: '12px',
  boxShadow:
    '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
  p: 2,
  minHeight: 104,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
} as const

export const homeSectionTitleSx = {
  fontWeight: 700,
  fontSize: '18px',
  lineHeight: '28px',
  color: homeHeading,
  mb: 2.5,
} as const

export const homeSurveysEmptyWrapSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  py: { xs: 3, md: 4 },
  px: 2,
  mb: 5,
} as const

export const homeSurveysEmptyIllustrationSx = {
  width: { xs: 200, sm: 240 },
  height: { xs: 200, sm: 240 },
  objectFit: 'contain',
  bgcolor: 'transparent',
  backgroundColor: 'transparent',
} as const

export const homeSurveysEmptyTitleSx = {
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '28px',
  color: homeHeading,
  mt: 0.5,
} as const

export const homeSurveysEmptySubtitleSx = {
  fontSize: '14px',
  lineHeight: '21px',
  color: homeMuted,
  mt: 0.5,
  maxWidth: 360,
} as const

/** MUI spacing units — gap before HomeMobilePromo (survey grid, rewards tab panel, etc.) */
export const providerContentAbovePromoSpacing = 5

export const providerContentAbovePromoGapSx = {
  mb: providerContentAbovePromoSpacing,
} as const

export const homeSurveyGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
  gap: '32px',
  ...providerContentAbovePromoGapSx,
} as const

export const homeSurveyCardSx = {
  bgcolor: surfaceAuthCard,
  border: `1px solid ${homeCardBorder}`,
  borderRadius: '12px',
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  minWidth: 0,
  width: '100%',
  overflow: 'hidden',
} as const

export const homeSurveyTitleSx = {
  fontWeight: 600,
  fontSize: '20px',
  lineHeight: '28px',
  color: homeHeading,
  minWidth: 0,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
} as const

export const homeSurveyDescriptionSx = {
  fontSize: '14px',
  lineHeight: '20px',
  color: homeMuted,
  minWidth: 0,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
} as const

export const homePillRowSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1.5,
  my: 0.5,
} as const

export const homeTimePillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  height: 35,
  px: '17px',
  borderRadius: '30px',
  border: `1.18px solid ${homeTimePillBorder}`,
  bgcolor: homeTimePillBg,
  color: homeTimePillText,
  fontSize: '12px',
  lineHeight: '16px',
} as const

export const homeEarnPillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1.5,
  height: 36,
  px: '25px',
  py: '17px',
  borderRadius: '30px',
  border: `1px solid ${homeEarnPillBorder}`,
  bgcolor: homeEarnPillBg,
  fontSize: '16px',
  lineHeight: '24px',
  color: homeEarnLabel,
} as const

export const homeStartSurveyButtonSx = {
  mt: 'auto',
  height: 45,
  borderRadius: '8px',
  boxShadow: homeButtonShadow,
  fontWeight: 600,
  fontSize: '13.6px',
  lineHeight: '23.8px',
  textTransform: 'none',
} as const

export const homeFooterSx = {
  flexShrink: 0,
  width: '100%',
  bgcolor: homeFooterBg,
  borderTop: `1px solid ${homeFooterBorder}`,
  py: 2.5,
  px: { xs: 2, md: 0 },
  mt: 'auto',
} as const

export const homeFooterTextSx = {
  fontSize: '14px',
  lineHeight: '20px',
  color: homeFooterText,
  textAlign: 'center',
  px: 2,
} as const
